// TODO: there's no endpoint to fetch a Loan by ID, will use a bottom sheet for now.

import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { Account, AccountsService } from '@treasury/domain/accounts';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-confirm-dialog';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import '../../components/jhd-header-bar';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/account-nicknames-table';
// eslint-disable-next-line no-duplicate-imports
import { type AccountNicknamesTable } from '../partials/account-nicknames-table';

export const tagName = 'account-nicknames-container';
@customElement(tagName)
export class AccountNicknamesContainer extends TmContainer {
    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private accountsService: AccountsService;

    @state()
    private accounts: Account[] = [];

    @state()
    private editedAccounts: Account[] = [];

    @state()
    private isBulkEditing = false;

    @state()
    private loadingResults = true;

    @state()
    private cancelConfirmDialogOpen = false;

    private readonly accountNicknamesTable = createRef<AccountNicknamesTable>();

    public async connectedCallback() {
        super.connectedCallback();
        await this.fetchAccounts();
        this.configureFooter();
    }

    protected async firstUpdated() {
        this.headerService.configure({
            title: 'Account Nicknames',
        });
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('isBulkEditing') || changedProperties.has('loading')) {
            this.configureFooter();
        }
    }

    protected render() {
        const loaderAndDialog = html`${this.renderLoader()}${this.renderUnsavedChangesCancelDialog()}`;
        const tableAndInstructions = html`<div class="flex flex-col h-full">
            <p class="m-6">
                Please provide a nickname for each account. Account Nicknames must be unique and are
                required.
            </p>
            <div class="grow">
                <account-nicknames-table
                    ${ref(this.accountNicknamesTable)}
                    class="h-full"
                    theme="light"
                    .accounts=${this.accounts}
                    .loading=${this.loadingResults}
                    .bulkEditMode=${this.isBulkEditing}
                    .isDesktop=${this.isDesktop}
                    @update-account=${(e: CustomEvent) => this.saveSingleAccount(e.detail)}
                    @changed-bulk-accounts=${(e: CustomEvent) => {
                        this.editedAccounts = e.detail;
                    }}
                ></account-nicknames-table>
            </div>
        </div>`;
        if (this.deviceService.isDesktop) {
            return html`${loaderAndDialog}<tm-section
                    class=${classMap({
                        'flex flex-col': true,
                        'overflow-hidden h-full': !this.isDesktop,
                        'mx-2 mb-12 mt-2 rounded-lg pb-[25px]': this.isDesktop,
                    })}
                    >${tableAndInstructions}</tm-section
                >`;
        }
        return html`${loaderAndDialog}<tm-body .padding=${false}>
                ${tableAndInstructions}
            </tm-body>`;
    }

    private renderUnsavedChangesCancelDialog() {
        return html`<tm-confirm-dialog
            .opened=${this.cancelConfirmDialogOpen}
            message=${`Are you sure you want to cancel the changes?`}
            cancelText="Continue Editing"
            cancel
            @cancel=${() => (this.cancelConfirmDialogOpen = false)}
            confirmText="Ok"
            confirmTheme="primary error"
            @confirm=${() => {
                this.cancelConfirmDialogOpen = false;
                this.editedAccounts = [];
                this.isBulkEditing = false;
            }}
        >
        </tm-confirm-dialog>`;
    }

    private configureFooter() {
        this.footerService.configure({
            direction: this.deviceService.isDesktop ? 'row' : 'column',
            buttonConfig: [
                {
                    text: 'Bulk Edit Nicknames',
                    importance: 'secondary',
                    classes: `w-auto ${this.isBulkEditing ? 'hidden' : ''}`,
                    onClick: () => {
                        this.editedAccounts = { ...this.accounts };
                        this.isBulkEditing = true;
                    },
                },
                {
                    text: 'Save',
                    importance: 'primary',
                    classes: `w-auto ${this.isBulkEditing ? '' : 'hidden'}`,
                    disabled: this.loading,
                    onClick: async () => {
                        await this.saveBulkAccounts();
                        this.isBulkEditing = false;
                    },
                },
                {
                    text: 'Cancel',
                    importance: 'secondary',
                    classes: `w-auto ${this.isBulkEditing ? '' : 'hidden'}`,
                    disabled: this.loading,
                    onClick: () => {
                        if (this.unsavedBulkAccountChanges) {
                            this.cancelConfirmDialogOpen = true;
                            return;
                        }
                        this.isBulkEditing = false;
                    },
                },
            ],
        });
    }

    private async fetchAccounts() {
        await this.tryFetch(
            async () => this.accountsService.getAccountNicknames(),
            result => {
                if (result) {
                    this.accounts = result;
                }
            }
        );
        this.loadingResults = false;
    }

    private get unsavedBulkAccountChanges() {
        return this.editedAccounts.some(editedAcc => {
            const originalAcc = this.accounts.find(acc => acc.id === editedAcc.id);
            return editedAcc.name !== originalAcc?.name;
        });
    }

    private async saveSingleAccount(account: Account) {
        this.loading = true;
        await this.tryFetch(
            () => this.accountsService.updateAccount(account),
            (data: Account | Error) => {
                if (data instanceof Error) {
                    this.notificationService.renderError(data.message);
                } else {
                    this.accounts = this.accounts.map(acc => (acc.id === data.id ? data : acc));
                }
                if (this.accountNicknamesTable.value) {
                    this.accountNicknamesTable.value.editedSingleAccount = undefined;
                }
                this.loading = false;
            },
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (err: any) => {
                this.notificationService.renderError(err);
                this.loading = false;
            }
        );
    }

    private async saveBulkAccounts() {
        this.loading = true;
        await this.tryFetch(
            () => this.accountsService.updateAccountNicknames(this.editedAccounts),
            (data: Account[] | Error) => {
                if (data instanceof Error) {
                    this.notificationService.renderError(data.message);
                } else {
                    this.accounts = data;
                }
                this.loading = false;
            },
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (err: any) => {
                this.notificationService.renderError(err);
                this.loading = false;
            }
        );
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountNicknamesContainer;
    }
}
